import { useEffect } from "react";
import { useFetchMeData } from "@common/features/User";
import notify from "@common/helpers/NotificationManager";
import { __ } from "@common/helpers/i18n";
import {
  getPwaNotificationPermission,
  isPwaStandalone,
  requestPwaNotificationPermission,
  supportsAppBadging,
  syncAppBadge,
} from "@common/helpers/pwa";

const PWA_BADGE_PERMISSION_DISMISSED_KEY = "pwa-badge-permission-dismissed";

export default function AppBadge() {
  const { data: unreadCount } = useFetchMeData((data) => {
    const inboxUnread = Number(data.masterUser?.inbox_unread || 0);
    const commentUnread = Number(data.masterUser?.comment_unread || 0);

    return Math.max(0, inboxUnread + commentUnread);
  });

  useEffect(() => {
    if (unreadCount === undefined) return;

    const updateBadge = () => {
      syncAppBadge(unreadCount).catch(() => undefined);
    };

    updateBadge();

    document.addEventListener("visibilitychange", updateBadge);
    window.addEventListener("focus", updateBadge);
    window.addEventListener("pageshow", updateBadge);

    return () => {
      document.removeEventListener("visibilitychange", updateBadge);
      window.removeEventListener("focus", updateBadge);
      window.removeEventListener("pageshow", updateBadge);
    };
  }, [unreadCount]);

  useEffect(() => {
    if (
      unreadCount === undefined ||
      unreadCount <= 0 ||
      !isPwaStandalone() ||
      !supportsAppBadging() ||
      getPwaNotificationPermission() !== "default"
    ) {
      return;
    }

    if (
      window.localStorage.getItem(PWA_BADGE_PERMISSION_DISMISSED_KEY) === "true"
    ) {
      return;
    }

    const dismiss = () => {
      window.localStorage.setItem(PWA_BADGE_PERMISSION_DISMISSED_KEY, "true");
    };

    const notification = notify.info({
      action: (
        <button
          type="button"
          className="h-9 rounded-md bg-primary px-3 text-sm font-extrabold text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={async () => {
            const permission = await requestPwaNotificationPermission();
            if (permission === "granted") {
              await syncAppBadge(unreadCount);
              notify.success(__("App badge enabled."));
            }

            if (notification) {
              notify.remove(notification);
            }
          }}
        >
          {__("Enable")}
        </button>
      ),
      message: __(
        "Allow notifications so unread counts can appear on the app icon.",
      ),
      onClose: dismiss,
      priority: true,
      timeout: false,
      title: __("Enable app icon badge"),
    });
  }, [unreadCount]);

  return null;
}
